<template>
    <Card :header="$t('salesByPriceRange')" :tooltip="$t('salesByPriceRangeTooltip')" overlay :loading="loading">
        <template #nav>
            <div :key="spaceId" class="nav-wrap gap-3">
                <DropdownFilter
                    v-model="priceRange"
                    :items="priceRanges"
                    :prefix="`${$t('priceRanges')}: `"
                    :disabled="loading"
                    :formatter="val => $t('everyXCurrency', { value: val, currency: currencySymbol })"
                />

                <Multiselect
                    :key="timeRangeString"
                    :fetch-options-function="fetchSources"
                    :placeholder="$t('sources')"
                    :selected-options-label="$t('selectedSources')"
                    preselect-all
                    searchable
                    track-by="id"
                    label="name"
                    select-label=""
                    :disabled="loading"
                    show-checkboxes
                    show-selected
                    multiple
                    :close-on-select="false"
                    :clear-on-select="false"
                    class="width-240"
                    @changeTrackBy="sources = $event"
                    @loading="sourcesLoading = $event"
                />

                <Multiselect
                    v-if="showProductGroupsResults"
                    :fetch-options-function="fetchGroups"
                    :placeholder="$t('productGroups')"
                    :selected-options-label="$t('selectedProductGroups')"
                    preselect-all
                    searchable
                    track-by="id"
                    label="name"
                    select-label=""
                    :disabled="loading"
                    show-checkboxes
                    show-selected
                    multiple
                    :close-on-select="false"
                    :clear-on-select="false"
                    class="width-240"
                    @changeTrackBy="groups = $event"
                    @loading="groupsLoading = $event"
                />

                <div class="w-100"></div>

                <b-form-checkbox v-model="showProductGroupsResults" switch :disabled="loading">
                    {{ $t('showProductGroupsResults') }}
                </b-form-checkbox>
            </div>
        </template>

        <ErrorMessage v-if="error" :value="error" class="mb-2" @dismissed="error = null" />

        <div class="d-flex flex-column gap-4">
            <EChart :options="valueOption" :auto-resize="true" style="width: 100%" />
            <EChart :options="quantityOption" :auto-resize="true" style="width: 100%" />
        </div>
    </Card>
</template>

<script>
    import Card from '@/components/common/Card.vue';
    import ErrorMessage from '@/components/common/ErrorMessage.vue';
    import DropdownFilter from '@/components/common/DropdownFilter.vue';
    import Multiselect from '@/components/common/Multiselect.vue';
    import EChart from '@/components/common/EChart.vue';
    import salesReportMixin from '../salesReportMixin';
    import { fetchSources, fetchGroups } from '../utils';

    export default {
        name: 'SalesPriceRange',
        mixins: [salesReportMixin],
        components: { Card, ErrorMessage, DropdownFilter, Multiselect, EChart },
        data() {
            return {
                requestProductDetails: this.$req('reportsSales/ProductDetails'),
                requestSources: this.$req('reportsSales/UniqueSources'),
                requestGroups: this.$req('producerProductGroups/SpaceGroups'),

                priceRange: 250,
                priceRanges: [10, 25, 50, 75, 100, 150, 200, 250],
                sources: [],
                groups: [],

                sourcesLoading: false,
                groupsLoading: false,

                showProductGroupsResults: false,
                rows: [],
                error: null,
            };
        },
        computed: {
            loading() {
                return this.requestProductDetails.getLoading() || this.sourcesLoading || this.groupsLoading;
            },
            currencySymbol() {
                const locale = process.env.VUE_APP_SYSTEM_LOCALE || this.$i18n.locale;
                const currency = process.env.VUE_APP_SYSTEM_CURRENCY;
                return (0).toLocaleString(locale, { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).replace(/\d/g, '').trim();
            },
            apiParams() {
                const { startDate, endDate } = this.timeRange || {};
                const urlParams = { spaceId: this.spaceId };
                const requestParams = { startDate, endDate };
                if (this.sources?.length) requestParams.sources = this.sources.join(',');
                if (this.showProductGroupsResults && this.groups?.length) {
                    requestParams.groups = this.groups.join(',');
                }
                return { urlParams, requestParams };
            },
            buckets() {
                const step = Math.max(1, Number(this.priceRange) || 1);
                const bins = new Map();

                for (const row of this.rows) {
                    const value = Number(row.value) || 0;
                    const quantity = Number(row.quantity) || 0;
                    const upper = Math.ceil(value / step) * step;
                    const lower = Math.max(0, upper - step);
                    const key = `${lower}-${upper}`;
                    if (!bins.has(key)) bins.set(key, { lower, upper, product: { value: 0, qty: 0 }, offer: { value: 0, qty: 0 } });
                    const bucket = bins.get(key);
                    const kind = row.type === 'product' ? 'product' : 'offer';
                    bucket[kind].value += value;
                    bucket[kind].qty += quantity;
                }

                return [...bins.values()].sort((bucketA, bucketB) => bucketA.upper - bucketB.upper);
            },
            valueOption() {
                if (!this.buckets.length) return null;
                return {
                    tooltip: {
                        trigger: 'axis',
                        valueFormatter: value => this.$format.currency(value),
                    },
                    legend: { data: [this.$t('manufacturerProducts'), this.$t('others')] },
                    grid: { bottom: 100 },
                    xAxis: { type: 'category', data: this.buckets.map(b => `${b.lower} - ${b.upper} ${this.currencySymbol}`), axisLabel: { rotate: 45, interval: 0 } },
                    yAxis: {
                        type: 'value',
                        name: this.$t('salesValue'),
                        axisLabel: { formatter: value => this.$format.currency(value, null, null, 0) },
                    },
                    series: [
                        { name: this.$t('manufacturerProducts'), type: 'bar', stack: 'total', emphasis: { focus: 'series' }, data: this.buckets.map(bucket => bucket.product.value) },
                        { name: this.$t('others'), type: 'bar', stack: 'total', emphasis: { focus: 'series' }, data: this.buckets.map(bucket => bucket.offer.value) },
                    ],
                };
            },
            quantityOption() {
                if (!this.buckets.length) return null;
                return {
                    tooltip: {
                        trigger: 'axis',
                        valueFormatter: value => this.$format.number(value, 0),
                    },
                    legend: { data: [this.$t('manufacturerProducts'), this.$t('others')] },
                    grid: { bottom: 100 },
                    xAxis: { type: 'category', data: this.buckets.map(bucket => `${bucket.lower} - ${bucket.upper} ${this.currencySymbol}`), axisLabel: { rotate: 45, interval: 0 } },
                    yAxis: { type: 'value', name: this.$t('numberOfItems'), axisLabel: { formatter: value => this.$format.number(value, 0) } },
                    series: [
                        { name: this.$t('manufacturerProducts'), type: 'bar', stack: 'total', emphasis: { focus: 'series' }, data: this.buckets.map(bucket => bucket.product.qty) },
                        { name: this.$t('others'), type: 'bar', stack: 'total', emphasis: { focus: 'series' }, data: this.buckets.map(bucket => bucket.offer.qty) },
                    ],
                };
            },
        },
        watch: {
            spaceId() {
                this.resetAndFetch();
            },
            apiParams: {
                deep: true,
                handler(newValue, oldValue) {
                    if (JSON.stringify(newValue || {}) !== JSON.stringify(oldValue || {})) this.loadData();
                },
            },
        },
        methods: {
            resetFilters() {
                this.priceRange = 250;
                this.showProductGroupsResults = false;
                this.resetAndFetch();
                this.loadData(true);
            },
            async resetAndFetch() {
                this.sources = [];
                this.groups = [];
                this.rows = [];
                this.requestProductDetails.abortAll?.();
                this.requestProductDetails.resetAll?.();
            },
            async loadData(force = false) {
                try {
                    if (!this.spaceId || !this.timeRange) return;
                    const { startDate, endDate } = this.timeRange;
                    const params = {
                        urlParams: { spaceId: this.spaceId },
                        requestParams: { startDate, endDate, conversionTime: null },
                    };
                    if (this.sources?.length) params.requestParams.sources = this.sources.join(',');
                    if (this.showProductGroupsResults && this.groups?.length) {
                        params.requestParams.groups = this.groups.join(',');
                    }
                    if (force) this.requestProductDetails.resetAll?.();
                    this.error = null;
                    const res = await this.requestProductDetails.get(params);
                    this.rows = Array.isArray(res)
                        ? res.map(r => ({
                              type: r.type === 'product' ? 'product' : 'offer',
                              value: Number(r.value) || 0,
                              quantity: Number(r.quantity) || 0,
                          }))
                        : [];
                } catch (error) {
                    this.error = error;
                    this.rows = [];
                }
            },
            async fetchSources({ search }) {
                return fetchSources({ requestSources: this.requestSources, spaceId: this.spaceId, timeRange: this.timeRange, search });
            },
            fetchGroups({ search }) {
                return fetchGroups({ requestGroups: this.requestGroups, spaceId: this.spaceId, search });
            },
        },
        created() {
            this.resetAndFetch();
        },
    };
</script>

<style scoped lang="scss">
    .nav-wrap {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        flex-wrap: wrap;
    }
</style>
